/**
 * Types for TotalConnectRTC and related call components
 * Centralized type definitions for the call system
 */

import { Room, LocalTrack, RemoteParticipant, Track } from "livekit-client";
import type { IncomingRingtonePlayback } from "@/lib/callSoundPreferences";

// ============================================
// Core Call Types
// ============================================

export type CallState =
  | "idle"
  | "initiating"
  | "ringing"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "ended";
export type CallType = "voice" | "video";
export type OutgoingCallProgress = "idle" | "connecting" | "calling" | "ringing";
export type ViewMode = "contacts" | "recents" | "incall" | "keypad" | "meetings";
export type TabType = "contacts" | "recents" | "keypad" | "meetings";
export type CallModalMode = "full" | "pabx-only" | "connect-only" | "none";
export type TransferType = "blind" | "attended";
export type AttendedTransferStatus =
  | "idle"
  | "ringing"
  | "consulting"
  | "connected";

// ============================================
// User & Contact Types
// ============================================

export interface OperationsUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
  role?: string;
  module_role?: string;
  operations_role?: string;
  is_field_worker?: boolean;
  is_online?: boolean;
  last_activity_at?: string;
  client_names?: string[];
  show_when_online?: boolean;
  has_active_session?: boolean;
  status?: string;
  personal_email?: string;
  primary_email?: string | null;
  // Cross-client partnership fields
  is_partner?: boolean;
  partner_client_id?: number;
  partner_client_name?: string;
}

export interface ContactDetails {
  user_id: number;
  first_name: string;
  last_name: string;
  display_name: string;
  email: string;
  avatar_url?: string;
  is_online: boolean;
  last_activity_at?: string;
  role?: string;
}

export interface PhoneContact {
  id: string;
  name: string;
  phoneNumbers: Array<{ number: string; type?: string }>;
}

/** CRM rows from email-contacts getCrmContacts — shared by dialer list + live suggestions */
export interface CrmDialContactRow {
  id: string | number;
  contact_id?: number;
  contact_person_id?: number;
  first_name?: string;
  last_name?: string;
  name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  company?: string;
  job_title?: string;
  contact_type?: string;
  is_company?: boolean;
  is_person?: boolean;
  crm_record_type?: 'company' | 'person';
}

// ============================================
// Call Log Types
// ============================================

export interface CallLog {
  _id: string;
  caller_id: number;
  caller_name: string;
  caller_number?: string;
  target_user_id?: number;
  target_user_name?: string;
  target_number?: string;
  call_type: "voice" | "video" | "sip_inbound" | "sip_outbound";
  status: "completed" | "missed" | "rejected" | "busy";
  started_at: string;
  ended_at?: string;
  duration_seconds?: number;
  is_sip_call?: boolean;
  direction?: "inbound" | "outbound";
  viewed?: boolean;
  viewed_at?: string | null;
}

// ============================================
// Participant Types
// ============================================

export interface CallParticipant {
  identity: string;
  name: string;
  userId: number;
  avatar_url?: string;
  isMuted: boolean;
  isSpeaking: boolean;
  hasVideo: boolean;
  isScreenSharing?: boolean;
  isHandRaised?: boolean;
  joinedAt?: Date;
  /** LiveKit participant attributes — SIP legs expose e.g. 'sip.callStatus' */
  attributes?: Record<string, string>;
}

export interface WaitingParticipant {
  userId: number;
  userName: string;
  guestIdentity?: string;
  guestEmail?: string;
}

// ============================================
// Activity & Reaction Types
// ============================================

export interface CallActivityEntry {
  id: string;
  type:
    | "join"
    | "leave"
    | "mute"
    | "unmute"
    | "video_on"
    | "video_off"
    | "screen_share_start"
    | "screen_share_stop"
    | "hand_raised"
    | "hand_lowered"
    | "reaction";
  participantName: string;
  participantId?: number;
  timestamp: Date;
  emoji?: string;
}

export interface CallReaction {
  id: string;
  emoji: string;
  userId: number;
  userName: string;
  timestamp: number;
}

export const CALL_REACTIONS = [
  { emoji: "👍", label: "Thumbs Up" },
  { emoji: "❤️", label: "Heart" },
  { emoji: "😂", label: "Laugh" },
  { emoji: "😮", label: "Surprised" },
  { emoji: "👏", label: "Clap" },
  { emoji: "🎉", label: "Celebration" },
] as const;

// ============================================
// Incoming Call Types
// ============================================

export interface IncomingCall {
  call_id: string;
  caller_id: number;
  caller_name: string;
  caller_avatar?: string;
  call_type: CallType;
  client_id?: number;
  thread_id?: string;
  thread_name?: string;
  timestamp?: string;
  autoAnswer?: boolean;
  // SIP-specific fields
  isSipCall?: boolean;
  caller_number?: string;
  sipData?: Partial<SipCallData>;
  /** Visitor-management gate call — resident can authorize/deny via keypad. */
  isGateCall?: boolean;
}

export interface PendingCallTarget {
  user: OperationsUser;
  callType: CallType;
}

export interface PendingGroupCall {
  threadId: string;
  threadName: string;
  callType: CallType;
  initiatorId?: number;
  callId?: string;
  participantIds?: number[];
  isMeeting?: boolean;
}

// ============================================
// SIP / PABX Types
// ============================================

export interface SipCallData {
  call_log_id?: string;
  provider_call_id?: string;
  /** Client ID that owns the SIP trunk — used for routing API calls to the correct SIP edge. */
  client_id?: number;
  livekit_room?: string;
  livekit_token?: string;
  livekit_host?: string;
  did_number?: string;
  extension_number?: string;
  /** Display label for routed extension targets (matches notify payload). */
  extension_display_name?: string;
  target_extension?: string;
  trunk_id?: string;
  trunk_name?: string;
  ring_group_id?: string;
  ring_group_name?: string;
  /** Server-defined strategy hint (e.g. simultaneous hunt). */
  ring_strategy?: string;
  ring_timeout?: number;
  caller_name?: string;
  caller_number?: string;
  queue_id?: string;
  queue_name?: string;
  queue_position?: number;
  /** Present when this is a PABX test call (welcome, echo, hold, courtesy, etc.) */
  test_mode?: string;
  /** User display name for personalized TTS greetings */
  user_display_name?: string;
  /** Client/company name for personalized TTS greetings */
  client_name?: string;
}

export interface SipExtension {
  id: number;
  extension_number: string;
  display_name: string;
  user_id?: number;
  do_not_disturb?: boolean;
  is_available?: boolean;
  // Rolling extension support
  friendly_extension?: string;
  rolling_enabled?: boolean;
  dynamic_extension?: string;
}

export interface ParkingSlot {
  slot: number;
  is_occupied: boolean;
  caller_name?: string;
  caller_number?: string;
  parked_by?: string;
  parked_at?: string;
}

export interface PickupGroupCall {
  call_id: string;
  caller_name: string;
  caller_number: string;
  ringing_extension: string;
  ringing_user_name: string;
  ring_started_at: Date;
}

export interface AttendedTransferState {
  status: AttendedTransferStatus;
  targetExtension?: string;
  targetName?: string;
  originalCallOnHold?: boolean;
}

export interface PendingTransfer {
  active: boolean;
  targetExtension?: string;
  targetName?: string;
  startedAt?: number;
}

/** Softphone-style multi-line strip (dual LiveKit lines when supported). */
export interface ActiveCallLine {
  id: string;
  label: string;
  subtitle?: string;
  state: "active" | "held" | "ringing" | "consultation";
  /** Which line is receiving keypad / hold / transfer actions */
  isFocused?: boolean;
  /** False = status-only row (e.g. unanswered call waiting); cannot tap to focus */
  selectable?: boolean;
}

// ============================================
// Call Settings Types
// ============================================

export interface CallSettings {
  do_not_disturb: boolean;
  call_waiting?: boolean;
  /** From users.preferences.call_settings — used by setPreferences merges. */
  preferred_device?: string;
  auto_answer_seconds?: number;
  speed_dials?: Array<{
    id: number;
    speed_dial_code: string;
    label: string;
    target_type: "extension" | "external";
    target_value: string;
  }>;
  forward_rules?: {
    always_forward?: { enabled: boolean; target: string };
    busy_forward?: { enabled: boolean; target: string };
    no_answer_forward?: { enabled: boolean; target: string };
  };
  sip_extension?: {
    id?: string;
    extension_number: string;
    display_name: string;
    trunk_name?: string;
    can_make_outbound_calls?: boolean;
    allowed_trunk_ids?: string[] | null;
    preferred_outbound_trunk_id?: string | null;
  };
  /** When true, inbound SIP rings are delivered only to native mobile (Capacitor) sessions, not web/desktop. */
  sip_inbound_mobile_only?: boolean;
  /** Persisted in users.preferences.call_settings — incoming Total Connect / SIP ringtone (see `/api/v1/call-settings`). */
  incoming_ringtone?: string | null;
  incoming_ringtone_volume?: number | null;
  /** DND status per accessible client (from /api/v1/call-settings GET). */
  dnd_across_clients?: { client_id: number; do_not_disturb: boolean }[];
  /** Effective DND across all accessible clients — true if any client has DND on. */
  dnd_effective_across_clients?: boolean;
}

export interface MeetingSettings {
  allowWhiteboard: boolean;
  allowRecording: boolean;
  enableLobby: boolean;
  autoAdmit: boolean;
  muteOnEntry: boolean;
  disableVideo: boolean;
}

// ============================================
// Session & Device Types
// ============================================

export interface ActiveSession {
  session_id: string;
  platform: "web" | "desktop" | "mobile";
  device_name: string;
  is_current: boolean;
  last_active_at?: string;
}

// ============================================
// Group Call / Meeting Types
// ============================================

export interface GroupCallInfo {
  threadId: string;
  threadName: string;
  callerName?: string;
  participantCount?: number;
  callType: CallType;
}

export interface BreakoutRoom {
  id: string;
  name: string;
  participants: Array<{ id: number; name: string; avatar?: string }>;
}

// ============================================
// Screen Pop Data (CRM Integration)
// ============================================

export interface ScreenPopData {
  phone_searched: string;
  contact?: {
    id: number;
    company_name?: string;
    contact_person?: string;
    position?: string;
    email?: string;
    photo_url?: string;
    logo_url?: string;
  };
  recent_tickets?: Array<{
    id: number;
    title: string;
    status: string;
  }>;
  recent_invoices?: Array<{
    id: string;
    item_number: string;
    grand_total: number;
    status: string;
  }>;
}

// ============================================
// Hook Return Types
// ============================================

export interface UseCallSessionReturn {
  // Room state
  room: Room | null;
  isConnecting: boolean;
  isConnected: boolean;
  connectionError: string | null;

  // Tracks
  localTracks: LocalTrack[];
  participants: CallParticipant[];

  // Actions
  connectToRoom: (
    roomName: string,
    callType: CallType,
    providedToken?: string,
    providedHost?: string,
  ) => Promise<boolean>;
  disconnectFromRoom: (reason?: string) => void;
  getLivekitToken: (
    roomName: string,
    callType: CallType,
  ) => Promise<string | null>;
  updateParticipants: () => void;
}

export interface UseCallControlsReturn {
  // State
  isMuted: boolean;
  isVideoEnabled: boolean;
  isSpeakerOn: boolean;
  canToggleSpeaker: boolean;
  isScreenSharing: boolean;
  isBlurEnabled: boolean;
  isOnHold: boolean;
  isHandRaised: boolean;
  isFrontCamera: boolean;
  audioOutputDevice: "speaker" | "earpiece" | "bluetooth";

  // Actions
  toggleMute: () => Promise<void>;
  toggleVideo: () => Promise<void>;
  toggleSpeaker: () => Promise<void>;
  toggleScreenShare: () => Promise<void>;
  toggleBlur: () => Promise<void>;
  toggleHold: () => Promise<void>;
  /** Dual-line / focus changes: align mute/hold UI with a specific LiveKit room (focused leg). */
  syncLocalParticipantUiFromRoom?: (
    targetRoom: Room | null,
    isOnHoldOverride: boolean,
  ) => void;
  toggleHandRaise: () => void;
  switchCamera: () => Promise<void>;
  sendReaction: (emoji: string) => void;
  enableAudio: () => Promise<void>;
  /** Sync video state from external sources (e.g. connectToRoom) */
  setVideoEnabled?: (enabled: boolean) => void;
}

export interface UseCallNotificationsReturn {
  // State
  isRinging: boolean;
  audioBlocked: boolean;

  // Actions
  playRingtone: (playback?: IncomingRingtonePlayback) => void;
  stopRingtone: () => void;
  playDialTone: () => void;
  stopDialTone: () => void;
  hapticFeedback: (style: "light" | "medium" | "heavy") => void;
  cancelCallNotification: () => Promise<void>;
  showCallNotification: (
    callerName: string,
    callType: CallType,
  ) => Promise<void>;
}

// ============================================
// Component Props Types
// ============================================

export interface CallAvatarProps {
  user?: OperationsUser | null;
  screenPopData?: ScreenPopData | null;
  isGroupCall?: boolean;
  isSipCall?: boolean;
  isVideoCall?: boolean;
  size?: "sm" | "md" | "lg" | "xl";
  showOnlineStatus?: boolean;
  showCallTypeBadge?: boolean;
  animate?: boolean;
  className?: string;
}

export interface CallTimerProps {
  callState: CallState;
  callDuration: number;
  isOnHold?: boolean;
  holdStartTime?: Date | null;
  className?: string;
  statusTextOverride?: string | null;
}

export interface ParticipantGridProps {
  participants: CallParticipant[];
  localParticipant?: {
    name: string;
    isMuted: boolean;
    isVideoEnabled: boolean;
    isHandRaised: boolean;
    avatarUrl?: string;
  };
  remoteVideoRefs: Map<string, HTMLVideoElement>;
  localVideoRef: React.RefObject<HTMLVideoElement>;
  showParticipantMenu?: boolean;
  selectedParticipantMenu?: string | null;
  onSelectParticipant?: (identity: string | null) => void;
  onRequestMute?: (identity: string, name: string) => void;
  onRemoveParticipant?: (identity: string, name: string) => void;
  participantHandRaises?: Map<number, boolean>;
  className?: string;
}
// Old InCallUIProps removed — use the Extended InCallUIProps below

export interface RingingUIProps {
  callType: CallType;
  selectedUser: OperationsUser | null;
  screenPopData: ScreenPopData | null;
  sipCallData: SipCallData | null;
  isGroupCall: boolean;
  groupCallInfo: GroupCallInfo | null;

  // Actions
  onAnswer: () => void;
  onReject: () => void;
  onRemindMe?: (minutes: number) => void;
  onQuickMessage?: (message: string) => void;
  onHapticFeedback: (style: "light" | "medium" | "heavy") => void;

  // Styling
  isDark: boolean;
  primaryColor: string;
}

// ============================================
// WebSocket Message Types
// ============================================

export interface CallWebSocketMessage {
  type: string;
  call_id?: string;
  user_id?: number;
  user_name?: string;
  caller_id?: number;
  caller_name?: string;
  caller_avatar?: string;
  call_type?: CallType;
  thread_id?: string;
  thread_name?: string;
  target_user_id?: number;
  livekit_token?: string;
  livekit_host?: string;
  reason?: string;
  emoji?: string;
  reaction_id?: string;
  element?: unknown; // Whiteboard element (validated at draw site)
  parking_slot?: number;
  parked_by?: string;
  target_identity?: string;
  requester_id?: number;
  requester_name?: string;
  rooms?: BreakoutRoom[];
  duration?: number;
  room_id?: string;
  target_session_id?: string;
}

// ============================================
// Additional UI Component Props
// ============================================

export type ContactsListSortMode = 'name_asc' | 'name_desc' | 'online_first';

export interface ContactsViewProps {
  users: OperationsUser[];
  loading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onStartCall: (user: OperationsUser, callType: CallType) => void;
  onSelectContact?: (user: OperationsUser) => void;
  isUserOnline: (user: OperationsUser) => boolean;
  getUserLastSeen: (user: OperationsUser) => string | null;
  isDark: boolean;
  primaryColor: string;
  currentUserId?: number;
  /** Device contacts (Capacitor) — opens phone picker */
  canAccessPhoneContacts?: boolean;
  onOpenPhoneContacts?: () => void;
  /** Parent renders search (e.g. Total Connect toolbar). */
  hideSearchBar?: boolean;
  /** Sort order for team list (default name A–Z). */
  sortMode?: ContactsListSortMode;
}

export type RecentsScope = 'all' | 'sip' | 'team';

export interface RecentsViewProps {
  callLogs: CallLog[];
  loading: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onCallBack: (userId: number, callType: CallType) => void;
  onDialNumber: (number: string) => void;
  currentUserId?: number;
  isDark: boolean;
  primaryColor: string;
  /** Controlled filter when parent owns toolbar dropdown. */
  recentsScope?: RecentsScope;
  onRecentsScopeChange?: (scope: RecentsScope) => void;
  /** Sort by call time (default newest). */
  recentsTimeSort?: 'newest' | 'oldest';
  /** Parent renders search + scope row. */
  hideRecentsToolbar?: boolean;
}

export type DialPadSuggestionSource = 'team' | 'crm' | 'recent';

export interface DialPadSuggestion {
  id: string;
  displayName: string;
  phoneDisplay: string;
  /** Value applied to the dial field when the row is tapped */
  dialString: string;
  phoneDigits: string;
  source: DialPadSuggestionSource;
  score: number;
}

export interface DialPadViewProps {
  extension: {
    extension_number: string;
    display_name: string;
    trunk_name?: string;
  } | null;
  dialNumber: string;
  onDialNumberChange: (number: string) => void;
  onDial: (number: string) => void;
  isDialing: boolean;
  /** When set, the main button becomes a red “cancel” control while `isDialing` (outbound in progress). */
  onCancelOutboundDial?: () => void;
  onOpenPhoneContacts?: () => void;
  canAccessContacts?: boolean;
  isDark: boolean;
  primaryColor: string;
  inCall?: boolean;
  onSendDtmf?: (digit: string) => void;
  /** Team contacts (left of call) and recents (right of call) — typical SIP idle dialer */
  onOpenTeamContacts?: () => void;
  onOpenRecents?: () => void;
  missedCallsCount?: number;
  /** Live matches (team phones, CRM, recents) while typing — Samsung-style */
  dialSuggestions?: DialPadSuggestion[];
  /** Extra bottom padding so SIP disclaimer clears Capacitor global footer (e.g. `80px`). */
  capacitorFooterClearance?: string;
}

export interface ActivityLogEntry {
  id: string;
  type:
    | "join"
    | "leave"
    | "mute"
    | "unmute"
    | "video_on"
    | "video_off"
    | "screen_share_start"
    | "screen_share_stop"
    | "hand_raised"
    | "hand_lowered"
    | "reaction";
  participantName: string;
  participantId?: number;
  timestamp: Date;
  emoji?: string;
}

export interface ActiveSession {
  session_id: string;
  platform: "web" | "desktop" | "mobile";
  device_name: string;
  is_current: boolean;
  last_active_at?: string;
}

// Extended InCallUI Props (with all handler props)
export interface InCallUIProps {
  // Core state
  callType: CallType;
  callState: CallState;
  callDuration: number;
  outgoingCallProgress?: OutgoingCallProgress;
  selectedUser: OperationsUser | null;
  screenPopData: ScreenPopData | null;
  sipCallData: SipCallData | null;
  isGroupCall: boolean;
  groupCallInfo: GroupCallInfo | null;
  participants: CallParticipant[];
  localTracksRef: React.RefObject<LocalTrack[]>;
  roomRef: React.RefObject<Room | null>;
  remoteVideoRefs: React.RefObject<Map<string, HTMLVideoElement>>;
  localVideoRef: React.RefObject<HTMLVideoElement | null>;

  // Control states
  isMuted: boolean;
  isVideoEnabled: boolean;
  isSpeakerOn: boolean;
  canToggleSpeaker: boolean;
  isOnHold: boolean;
  isBlurEnabled: boolean;
  isScreenSharing: boolean;
  isHandRaised: boolean;
  isRecording: boolean;
  recordingStartTime: Date | null;

  // Remote states
  remoteScreenShareActive: boolean;
  remoteVideoActive: boolean;
  isFrontCamera: boolean;
  audioBlocked: boolean;

  // Actions
  onToggleMute: () => void;
  onToggleVideo: () => void;
  onToggleSpeaker: () => void;
  onToggleHold: () => void;
  onToggleBlur: () => void;
  onToggleScreenShare: () => void;
  onToggleHandRaise: () => void;
  onToggleRecording: () => void;
  onSwitchCamera: () => void;
  onSendReaction: (emoji: string) => void;
  onEnableAudio: () => void;
  onEndCall: () => void;
  onPopOut: () => void;
  onTransfer: (extensionNumber: string, displayName: string) => void;
  onParkCall: (slot: number) => void;
  onAddParticipant: (user: OperationsUser) => void;
  addingParticipant?: number | null;
  onGenerateInviteLink?: () => void;
  generatingLink?: boolean;
  showInviteLink?: boolean;
  inviteLink?: string | null;
  onCopyInviteLink?: () => void;
  onCloseInviteLink?: () => void;
  onHapticFeedback: (style: "light" | "medium" | "heavy") => void;

  // UI state
  isDark: boolean;
  primaryColor: string;

  // Modals/panels state
  showParticipantsPanel: boolean;
  onToggleParticipantsPanel: (show: boolean) => void;
  showActivityLog: boolean;
  onToggleActivityLog: (show: boolean) => void;
  activityLog: ActivityLogEntry[];

  // Active reactions
  activeReactions: CallReaction[];

  // Participant hand raises
  participantHandRaises: Map<number, boolean>;
  handsRaisedCount: number;

  // Waiting room
  waitingParticipants: CallParticipant[];
  onAdmitParticipant?: (participantId: number) => void;
  onDenyParticipant?: (participantId: number) => void;

  // Breakout rooms
  isBreakoutActive: boolean;
  breakoutRooms: BreakoutRoom[];
  onShowBreakoutModal: () => void;

  // Whiteboard
  meetingSettingsConfig?: MeetingSettings;
  showWhiteboard: boolean;
  onToggleWhiteboard: (show: boolean) => void;
  /** Another participant has shared their board session (pulse Board when not viewing). */
  remotePresenterBoardActive?: boolean;
  remotePresenterBoardName?: string;

  // SIP-specific props
  isSipCall: boolean;
  /** Visitor-management gate call — show resident authorize/deny controls. */
  isGateCall?: boolean;
  onSendDtmf?: (digit: string) => void;
  onShowKeypad?: () => void;

  // Device switcher
  activeSessions: ActiveSession[];
  loadingSessions: boolean;
  onTransferToDevice: (sessionId: string) => void;
  transferringTo: string | null;

  // Extension list for transfer
  extensionList: SipExtension[];
  loadingExtensions: boolean;

  // Attended transfer state (shows transfer-in-progress / consultation overlay)
  attendedTransferState?: AttendedTransferState;
  pendingTransfer?: PendingTransfer;
  onCompleteTransfer?: () => void;
  /** After consultation: bridge caller audio back and stay on the call with all parties (same LiveKit room). */
  onMergeConference?: () => void;
  /** Attended transfer API in flight — disables duplicate taps on merge / complete. */
  attendedTransferSubmitting?: 'merge' | 'complete' | null;
  onCancelTransfer?: () => void;
  onCancelAttendedTransfer?: () => void;

  /** Inbound SIP on second line while line 1 is connected */
  secondLineIncoming?: IncomingCall | null;
  onAnswerSecondLine?: () => void | Promise<void>;
  onDeclineSecondLine?: () => void | Promise<void>;

  activeCallLines?: ActiveCallLine[];
  onSelectActiveCallLine?: (lineId: string) => void;

  // Operations users for transfer dialog (contacts-style)
  operationsUsers?: OperationsUser[];
  isUserOnline?: (user: OperationsUser) => boolean;

  // Parking slots
  parkingSlots: ParkingSlot[];
  loadingParkingSlots: boolean;

  // Participant menu
  selectedParticipantMenu: string | null;
  onSelectParticipantMenu: (identity: string | null) => void;
  onRequestParticipantMute: (identity: string, name: string) => void;
  onRemoveParticipant: (identity: string, name: string) => void;

  // Call start time (for debounce)
  callStartTime: number;
  /** Embedded mode fills the parent desktop suite instead of using a viewport-fixed overlay. */
  layoutVariant?: 'slideover' | 'embedded';
}

// ============================================
// Platform Detection Helpers
// ============================================

export type Platform = "web" | "capacitor" | "tauri";

export interface PlatformInfo {
  platform: Platform;
  isIOS: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  isDesktop: boolean;
}
